import React, { FC } from 'react';
import { memo, useMemo } from 'react';

import KeypointCollectionOption from './KeypointCollectionOption';
import KeypointOption from './KeypointOption';
import {
  FeatureContainer,
  FeatureOptionsContainer,
  FeatureTitle,
  LabelsContainer,
} from './Labels.style';
import PhaseOption from './PhaseOption';
import { useNavigate, useParams } from 'react-router-dom';

interface IProps {
  dicom: any;
  activeFrame: any;
  activeFrameIndex: any;
  phaseLabels: any;
  setActiveFrameIndex: any;
  activeKeypoints: any;
  setKeypoints: any;
  keypointCollectionClasses: any;
  activeKeypointCollectionClass: any;
  setActiveKeypointCollectionClass: any;
  keypointCollectionLabels: any;
  showPhaser: any;
}

const Labels: FC<IProps> = props => {
  const {
    dicom,
    activeFrame,
    activeFrameIndex,
    phaseLabels,
    setActiveFrameIndex,
    activeKeypoints,
    setKeypoints,
    keypointCollectionClasses,
    activeKeypointCollectionClass,
    setActiveKeypointCollectionClass,
    keypointCollectionLabels,
    showPhaser,
  } = props;
  const phaseLabelOptions = phaseLabels?.map((phaseLabel, index) => {
    const navigate = useNavigate();
    const { studyIcid, dicomIcid } = useParams();

    const isActive = activeFrameIndex === phaseLabel?.frameIndex;

    const keypointCollectionClassesForPhase = keypointCollectionClasses?.filter(
      keypointCollectionClass =>
        keypointCollectionClass?.phases
          ?.map(phase => phase?.value)
          ?.includes(phaseLabel?.phaseClass)
    );
    const keypointCollectionLabelsForPhase = keypointCollectionLabels?.filter(
      keypointCollectionLabel =>
        keypointCollectionLabel?.frame === phaseLabel?.frame &&
        keypointCollectionClassesForPhase
          ?.map(keypointCollectionClass => keypointCollectionClass?.uuid)
          ?.includes(keypointCollectionLabel?.keypointCollectionClass)
    );
    const requiredKeypointCollectionClassesForPhase = keypointCollectionClassesForPhase?.filter(
      keypointCollectionClass => keypointCollectionClass?.isRequired !== false
    );
    const isLabeled = requiredKeypointCollectionClassesForPhase?.every(keypointCollectionClass => {
      const keypointCollectionLabel = keypointCollectionLabelsForPhase?.find(
        keypointCollectionLabel =>
          keypointCollectionLabel?.keypointCollectionClass === keypointCollectionClass?.uuid
      );
      const keypointClasses = keypointCollectionClass?.keypointClasses;
      const isLabeled = keypointClasses?.every(keypointClass => {
        const keypointLabel = keypointCollectionLabel?.keypointLabels?.find(
          keypointLabel => keypointLabel?.keypointClass === keypointClass?.uuid
        );
        return keypointLabel?.x && keypointLabel?.y;
      });
      return isLabeled;
    });

    const handlePhaseOptionOnClick = () => {
      setActiveFrameIndex(phaseLabel?.frameIndex);
      navigate(`/${studyIcid}/${dicomIcid}/${phaseLabel?.frameIcid}`, { replace: true });
    };

    return (
      <PhaseOption
        key={index}
        phaseLabel={phaseLabel}
        isActive={isActive}
        isLabeled={isLabeled}
        handlePhaseOptionOnClick={handlePhaseOptionOnClick}
      />
    );
  });

  const keypointCollectionClassOptions = useMemo(() => {
    const keypointCollectionHotkeys = ['Q', 'W', 'E', 'R', 'T', 'Y'];
    return keypointCollectionClasses
      ?.filter(keypointCollectionClass => {
        const phasesForClass = keypointCollectionClass?.phases?.map(phase => phase?.value);

        // only check for phases if the dicom has multiple frames
        const keypointCollectionClassHasPhases =
          dicom?.numberOfFrames > 1 && phasesForClass?.length > 0;

        if (!keypointCollectionClassHasPhases) return true;

        const activePhase = phaseLabels?.find(
          phaseLabel => phaseLabel?.frame === activeFrame?.uuid
        );
        const activePhaseClass = activePhase?.phaseClass;
        const activePhaseIsInKeypointCollectionClass = phasesForClass?.includes(activePhaseClass);
        const classIsActive = activeKeypointCollectionClass?.uuid === keypointCollectionClass?.uuid;

        // special case to set a non valid keypoint collection class to be inactive
        if (!activePhaseIsInKeypointCollectionClass && classIsActive) {
          setActiveKeypointCollectionClass(null);
        }

        return activePhaseIsInKeypointCollectionClass;
      })
      ?.map((keypointCollectionClass, index) => {
        const isActive = activeKeypointCollectionClass?.uuid === keypointCollectionClass?.uuid;
        const keypointCollectionLabel = keypointCollectionLabels?.find(
          keypointCollectionLabel =>
            keypointCollectionLabel?.keypointCollectionClass === keypointCollectionClass?.uuid &&
            keypointCollectionLabel?.frame === activeFrame?.uuid
        );
        const keypointLabels = keypointCollectionLabel?.keypointLabels;
        const isLabeled =
          keypointLabels?.length > 0 &&
          keypointLabels?.every(keypoint => keypoint?.x && keypoint?.y);
        return (
          <KeypointCollectionOption
            key={keypointCollectionClass?.uuid}
            hotkey={keypointCollectionHotkeys[index]}
            isActive={isActive}
            isLabeled={isLabeled}
            keypointCollectionClass={keypointCollectionClass}
            setActiveKeypointCollectionClass={setActiveKeypointCollectionClass}
          />
        );
      });
  }, [
    activeFrame?.uuid,
    activeKeypointCollectionClass?.uuid,
    keypointCollectionClasses,
    keypointCollectionLabels,
    phaseLabels,
    setActiveKeypointCollectionClass,
  ]);

  const keypointClassOptions = useMemo(() => {
    if (!activeKeypointCollectionClass?.keypointClasses) return null;
    if (!Array.isArray(activeKeypoints)) return null;

    return activeKeypointCollectionClass.keypointClasses.map((keypointClass, index) => {
      const keypointIndex = keypointClass.order - 1;
      const keypoint = activeKeypoints.find(
        keypoint => keypoint?.keypointClass === keypointClass?.uuid
      );

      const setKeypointIsActive = isActive => {
        const newKeypoints = activeKeypoints.map(kp => ({ ...kp, isActive: false }));
        setKeypoints([
          ...newKeypoints.slice(0, keypointIndex),
          { ...newKeypoints[keypointIndex], isActive },
          ...newKeypoints.slice(keypointIndex + 1),
        ]);
      };

      return (
        <KeypointOption
          key={index}
          keypointClass={keypointClass}
          keypoint={keypoint}
          setKeypointIsActive={setKeypointIsActive}
        />
      );
    });
  }, [activeKeypointCollectionClass?.keypointClasses, activeKeypoints, setKeypoints]);

  return (
    <LabelsContainer>
      {showPhaser && (
        <FeatureContainer>
          <FeatureTitle>Phase</FeatureTitle>
          <FeatureOptionsContainer>{phaseLabelOptions}</FeatureOptionsContainer>
        </FeatureContainer>
      )}
      <FeatureContainer>
        <FeatureTitle>Keypoint Collections</FeatureTitle>
        <FeatureOptionsContainer>{keypointCollectionClassOptions}</FeatureOptionsContainer>
      </FeatureContainer>
      <FeatureContainer>
        <FeatureTitle>Keypoints</FeatureTitle>
        <FeatureOptionsContainer>{keypointClassOptions}</FeatureOptionsContainer>
      </FeatureContainer>
    </LabelsContainer>
  );
};

export default memo(Labels);
